import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";

import Humanize from "humanize-plus";

import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  paperClasses,
  Stack,
  styled,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { differenceInDays } from "date-fns";
import { toast } from "react-toastify";
import { MIN_CONTENT_WIDTH } from "../../constants/layout";
import FileArrowDown from "../../icons/FileArrowDown.svg?react";
import { exists } from "../../lib/Util";
import { downloadDirtyRows, getFirstNDirtyRows } from "../../networking/apiMethods";
import typography from "../../styles/sbDesignSystem/sbTypography";
import { JsonViewer } from "../JSONViewer/JSONViewer";
import { HumanRows, toPercentValue } from "./Formatters";
import { StatusIcon } from "./StatusIcon";

const MAX_FIRST_N_DIRTY_ROWS = 100;

const StyledChip = styled(Chip)(({ theme }) => ({
  padding: "0px 8px",
  height: "20px",
  width: "fit-content",
  lineHeight: "16px",
  color: theme.palette.text10.main,
  backgroundColor: theme.palette.text40.main,
  borderRadius: "6px",
  marginLeft: "4px",
  ...(typography && {
    ...typography["body.xSmall.regular"],
  }),
  fontSize: "10px",
}));

const DynamicHoverStyledChip = styled(StyledChip)(({ theme }) => ({
  span: {
    color: theme.palette.text10.main,
  },
  backgroundColor: "currentColor",
}));

const CombinedStatusContainer = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  ".status-col1": {
    flexGrow: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  ".status-col2 ": {
    paddingRight: theme.spacing(0.25),
  },
}));

const StatusColumn = ({ rowCount, status }: { rowCount?: number | null; status?: string }) => {
  return (
    <CombinedStatusContainer>
      <div className="status-col1">
        <HumanRows rowCount={rowCount} testLabel={"total-rows"} showArrow={true} showTooltip={true} />
      </div>
      <div className="status-col2" data-cy={status}>
        <StatusIcon status={status} width="14px" height="14px" />
      </div>
    </CombinedStatusContainer>
  );
};

type DirtyRowsLabelProps = Pick<
  CompletedStatusColumnProps,
  "rowCount" | "dirtyRowStatus" | "dirtyRowCount" | "downloadPartId" | "onToggleDetailPanel"
> & { finishedDateExceeds30days: boolean; newMethod?: boolean };

type DownloadDirtyFilesProps = Pick<
  CompletedStatusColumnProps,
  "dirtyRowStatus" | "downloadPartId" | "dirtyRowCount" | "onToggleDetailPanel"
> & {
  finishedDateExceeds30days: boolean;
  children: ReactNode;
  newMethod?: boolean;
};

export type CompletedStatusColumnProps = {
  rowCount?: number | null;
  dirtyRowStatus?: string | null;
  newDirtyRowStatus?: string | null;
  dirtyRowCount?: number | null;
  newDirtyRowCount?: number | null;
  downloadPartId?: string | null;
  status?: string;
  finished?: string | null;
  statusColumnWidth?: number;
  onToggleDetailPanel?: () => void;
};

type RowCountDetailProps = CompletedStatusColumnProps & {
  finishedDateExceeds30days: boolean;
  statusColumnWidth?: number;
};

const RowCountDetail = forwardRef<HTMLDivElement, RowCountDetailProps>(
  (
    {
      downloadPartId,
      finishedDateExceeds30days,
      rowCount,
      dirtyRowCount,
      dirtyRowStatus,
      status,
      newDirtyRowCount,
      newDirtyRowStatus,
      statusColumnWidth,
      onToggleDetailPanel,
      ...rest
    },
    ref
  ) => {
    const dirtyRowsRef = useRef<HTMLDivElement | null>(null);
    const newDirtyRowsRef = useRef<HTMLDivElement | null>(null);
    const [areMethodsFittingInOneLine, setAreMethodsFittingInOneLine] = useState(false);

    useEffect(() => {
      if (dirtyRowsRef.current && newDirtyRowsRef.current) {
        const y1 = dirtyRowsRef.current.getBoundingClientRect().y;
        const y2 = newDirtyRowsRef.current.getBoundingClientRect().y;
        setAreMethodsFittingInOneLine(y1 === y2);
      }
    }, [statusColumnWidth]);

    return (
      <Grid container {...rest} ref={ref} sx={{ alignItems: "center", justifyContent: "flex-end" }}>
        <Grid container sx={{ alignItems: "center", justifyContent: "flex-end" }}>
          <HumanRows rowCount={rowCount} testLabel={"total-rows"} showTooltip={false} showArrow={true} />
          <div data-cy={status}>
            <StatusIcon status={status} large={false} />
          </div>
        </Grid>
        <Grid container sx={{ alignItems: "center", justifyContent: "flex-end", mr: 0.8 }}>
          <div ref={dirtyRowsRef}>
            <DirtyRowsLabel
              downloadPartId={downloadPartId}
              finishedDateExceeds30days={finishedDateExceeds30days}
              rowCount={rowCount}
              dirtyRowCount={dirtyRowCount}
              dirtyRowStatus={dirtyRowStatus}
              onToggleDetailPanel={onToggleDetailPanel}
            />
          </div>
          {dirtyRowCount && newDirtyRowCount && areMethodsFittingInOneLine ? (
            <Typography
              sx={{
                margin: "4px",
                fontSize: "12px",
                fontWeight: "bold",
                lineHeight: "10px",
                paddingX: "12px",
                color: "text20.main",
              }}
            >
              /
            </Typography>
          ) : null}
          <div ref={newDirtyRowsRef}>
            <DirtyRowsLabel
              downloadPartId={downloadPartId}
              finishedDateExceeds30days={finishedDateExceeds30days}
              rowCount={rowCount}
              dirtyRowCount={newDirtyRowCount}
              dirtyRowStatus={newDirtyRowStatus}
              newMethod={true}
              onToggleDetailPanel={onToggleDetailPanel}
            />
          </div>
        </Grid>
      </Grid>
    );
  }
);
RowCountDetail.displayName = "RowCountDetail";

const DirtyRowsStatusColumn = ({
  finished,
  rowCount,
  downloadPartId,
  dirtyRowCount,
  dirtyRowStatus,
  status,
  newDirtyRowStatus,
  newDirtyRowCount,
  statusColumnWidth,
  onToggleDetailPanel,
}: CompletedStatusColumnProps) => {
  const today = new Date();

  const finishedDateExceeds30days = !finished || differenceInDays(today, new Date(finished)) > 30;

  return (
    <Tooltip
      id={"dirty_rows_status"}
      title={
        <DirtyRowsTooltipContent
          finishedDateExceeds30days={finishedDateExceeds30days}
          rowCount={rowCount}
          dirtyRowCount={dirtyRowCount}
          dirtyRowStatus={dirtyRowStatus}
          newDirtyRowCount={newDirtyRowCount}
          newDirtyRowStatus={newDirtyRowStatus}
        />
      }
      placement="left"
      arrow
    >
      <RowCountDetail
        downloadPartId={downloadPartId}
        rowCount={rowCount}
        data-cy="tooltip-dirty-row-container"
        status={status}
        finished={finished}
        finishedDateExceeds30days={finishedDateExceeds30days}
        dirtyRowCount={dirtyRowCount}
        dirtyRowStatus={dirtyRowStatus}
        newDirtyRowCount={newDirtyRowCount}
        newDirtyRowStatus={newDirtyRowStatus}
        statusColumnWidth={statusColumnWidth}
        onToggleDetailPanel={onToggleDetailPanel}
      />
    </Tooltip>
  );
};

const getDirtyRowElement = (
  rowCount: number,
  dirtyRowCount?: number | null,
  dirtyRowStatus?: string | null,
  isNew?: boolean
) => {
  if (dirtyRowStatus === "some") {
    if (exists(dirtyRowCount)) {
      const cleanRows = rowCount - dirtyRowCount;
      const percentCleanRows = toPercentValue({ n: cleanRows, d: rowCount });
      const cleanRowsText = `${Humanize.intComma(cleanRows)} (${percentCleanRows}%) clean rows.`;
      const percentDirtyRows = toPercentValue({ n: dirtyRowCount, d: rowCount });
      const dirtyRowsText = `${Humanize.intComma(dirtyRowCount)} (${percentDirtyRows}%) dirty rows.`;
      return (
        <>
          <br />
          {cleanRowsText}
          {isNew ? <StyledChip label="New" /> : null}
          <br />
          {dirtyRowsText}
          {isNew ? <StyledChip label="New" /> : null}
        </>
      );
    } else {
      return (
        <>
          <br />
          {`Some dirty rows.`}
          {isNew ? <StyledChip label="New" /> : null}
        </>
      );
    }
  }
  if (dirtyRowStatus === "all") {
    return (
      <>
        <br />
        {"0 (0%) clean rows"}
        {isNew ? <StyledChip label="New" /> : null}
        <br />
        {`${rowCount} (100%) dirty rows`}
        {isNew ? <StyledChip label="New" /> : null}
      </>
    );
  }
  return null;
};

const DirtyRowsTooltipContent = ({
  rowCount,
  dirtyRowStatus,
  dirtyRowCount,
  finishedDateExceeds30days,
  newDirtyRowCount,
  newDirtyRowStatus,
}: Pick<
  CompletedStatusColumnProps,
  "rowCount" | "dirtyRowStatus" | "dirtyRowCount" | "newDirtyRowStatus" | "newDirtyRowCount"
> & {
  finishedDateExceeds30days: boolean;
}) => {
  const dirtyRowsDeadlineDisclaimer = (
    <>
      <br />
      Downloading files over 30 days old is not supported.
    </>
  );
  const defaultTooltipText = rowCount
    ? `${Humanize.intComma(rowCount)} total rows.`
    : `Row count couldn't be determined`;

  const isDirtyEmpty = dirtyRowStatus === "none" || dirtyRowCount === 0;
  const isNewDirtyEmpty = newDirtyRowStatus === "none" || newDirtyRowCount === 0;

  if (!rowCount || (isDirtyEmpty && isNewDirtyEmpty)) {
    return <div>{defaultTooltipText}</div>;
  }

  const areSomeDirtyNoCount = dirtyRowStatus === "some" && !exists(dirtyRowCount);
  const areSomeNewDirtyNoCount = newDirtyRowStatus === "some" && !exists(newDirtyRowCount);

  if (areSomeDirtyNoCount && areSomeNewDirtyNoCount) {
    return <div>{`${Humanize.intComma(rowCount)} total rows, with some dirty rows.`}</div>;
  }

  const dirtyRowsComponent = getDirtyRowElement(rowCount, dirtyRowCount, dirtyRowStatus);
  const newDirtyRowsComponent = getDirtyRowElement(rowCount, newDirtyRowCount, newDirtyRowStatus, true);

  return (
    <div>
      {defaultTooltipText}
      {dirtyRowsComponent}
      {newDirtyRowsComponent}
      {finishedDateExceeds30days && dirtyRowsDeadlineDisclaimer}
    </div>
  );
};

const DownloadDirtyFiles = ({
  finishedDateExceeds30days,
  downloadPartId,
  dirtyRowStatus,
  children,
  newMethod,
  dirtyRowCount,
  onToggleDetailPanel,
}: DownloadDirtyFilesProps) => {
  const { getAccessTokenSilently } = useAuth0();
  const [showFirstDirtyRows, setShowFirstDirtyRows] = useState(false);
  const [dirtyRows, setDirtyRows] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleClick = async () => {
    if (!downloadPartId && onToggleDetailPanel) return onToggleDetailPanel();
    else if (newMethod) return onClickDownloadFirstNRows();
    else return downloadAllRows();
  };
  const downloadAllRows = async () => {
    if (!downloadPartId) {
      toast.error("There was an error downloading the rows");
      console.error("There was a dirty row download button with no downloadPartId associated");
      return;
    }
    const response = await downloadDirtyRows(downloadPartId, getAccessTokenSilently, newMethod);
    const url = response.data;
    const a = document.createElement("a");
    a.href = url;
    const clickEvt = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  };

  const firstNDirtyRows = Math.min(dirtyRowCount || 0, MAX_FIRST_N_DIRTY_ROWS);

  const downloadFirstNRows = async () => {
    if (!downloadPartId) {
      toast.error("There was an error downloading the rows");
      console.error("There was a dirty row download button with no downloadPartId associated");
      return;
    }
    setLoading(true);
    try {
      const response = await getFirstNDirtyRows(downloadPartId, getAccessTokenSilently, firstNDirtyRows);
      setLoading(false);
      setDirtyRows(response.data);
    } catch (e) {
      toast.error("There was an error downloading the rows");
      console.error("There was an error downloading the rows", e);
      setShowFirstDirtyRows(false);
      return;
    }
  };

  const onClickDownloadFirstNRows = async () => {
    downloadFirstNRows();
    setShowFirstDirtyRows(true);
  };

  const displayedDirtyRowsText = `Displaying ${dirtyRows.length}/${dirtyRowCount} dirty rows`;

  return (
    <>
      <DownloadDirtyFilesStyled
        finishedDateExceeds30days={finishedDateExceeds30days}
        onClick={(e) => {
          e.stopPropagation();
        }}
        dirtyRowStatus={dirtyRowStatus}
        newMethod={newMethod}
      >
        <Button
          onClick={handleClick}
          disabled={finishedDateExceeds30days}
          sx={{ padding: 0, height: "fit-content" }}
          data-testid={newMethod ? "download-new-dirty-rows" : "download-dirty-rows"}
          variant="text"
        >
          <Stack direction="row" sx={{ alignItems: "center" }}>
            {children}

            {newMethod ? <EyeIcon width="20px" height="20px" /> : <FileArrowDown width="20px" height="20px" />}
          </Stack>
          <DynamicHoverStyledChip label={newMethod ? "New" : "Current"} />
        </Button>
      </DownloadDirtyFilesStyled>
      <Dialog
        open={showFirstDirtyRows}
        onClose={() => setShowFirstDirtyRows(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.newDirtyRowsModal,
          margin: "16px",
          [`& .${paperClasses.root}`]: { borderRadius: "16px" },
        }}
        fullScreen
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {loading ? "Loading dirty rows..." : displayedDirtyRowsText}

          <Stack direction="row" spacing="16px" sx={{ alignItems: "center" }}>
            <IconButton
              onClick={() => {
                downloadAllRows();
                setShowFirstDirtyRows(false);
              }}
              sx={(theme) => ({ color: theme.palette.text50.main })}
            >
              <DownloadSimpleIcon size={24} />
            </IconButton>
            <IconButton
              onClick={() => setShowFirstDirtyRows(false)}
              sx={(theme) => ({ color: theme.palette.text50.main })}
            >
              <XIcon size={24} />
            </IconButton>
          </Stack>
        </DialogTitle>

        {loading ? (
          <Stack sx={{ height: "100%", width: "100%", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress />
          </Stack>
        ) : (
          <DialogContent sx={{ maxHeight: "90%" }}>
            <Stack
              sx={{
                gap: "18px",
                borderRadius: "4px",
                minHeight: "66px",
              }}
            >
              {dirtyRows.map((row: string, index: number) => {
                return (
                  <Stack
                    direction="row"
                    sx={(theme) => ({
                      border: ` 4px solid ${theme.palette.surfaceLight40.main}`,
                      borderRadius: "4px",
                      overflowX: "auto",
                      minWidth: MIN_CONTENT_WIDTH,
                    })}
                    key={`dirty-row-${index}`}
                  >
                    <Stack
                      sx={{
                        backgroundColor: (theme) => theme.palette.surfaceLight40.main,
                        padding: "8px",
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                      }}
                    >
                      {index + 1}
                    </Stack>

                    <JsonViewer data={JSON.parse(row)} />
                  </Stack>
                );
              })}
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

const getHoverColor = (theme: Theme, dirtyRowStatus?: string | null, finishedDateExceeds30days?: boolean) => {
  if (finishedDateExceeds30days) return theme.palette.text30.main;
  if (dirtyRowStatus === "some") return theme.palette.accentWarning40.main;
  return theme.palette.accentDanger40.main;
};

const getColor = (theme: Theme, dirtyRowStatus?: string | null, finishedDateExceeds30days?: boolean) => {
  if (finishedDateExceeds30days) return theme.palette.text20.main;
  if (dirtyRowStatus === "some") return theme.palette.accentWarning30.main;
  return theme.palette.accentDanger30.main;
};

type DownloadDirtyFilesStyledProps = Pick<CompletedStatusColumnProps, "dirtyRowStatus"> & {
  finishedDateExceeds30days: boolean;
  newMethod?: boolean;
};

const DownloadDirtyFilesStyled = styled("div", {
  shouldForwardProp: (prop) => prop !== "dirtyRowStatus",
})<DownloadDirtyFilesStyledProps>(({ dirtyRowStatus, theme, finishedDateExceeds30days, newMethod }) => ({
  color: dirtyRowStatus === "some" ? theme.palette.warning.main : theme.palette.error.main,
  cursor: "pointer",
  display: "inline-block",
  svg: {
    marginLeft: theme.spacing(0.5),
  },
  "&:hover": {
    div: {
      textDecoration: "none",
      color: newMethod ? getHoverColor(theme, dirtyRowStatus, finishedDateExceeds30days) : theme.palette.text50.main,
    },
  },
  div: {
    color: newMethod ? getColor(theme, dirtyRowStatus, finishedDateExceeds30days) : theme.palette.text40.main,
    fontSize: "12px",
  },
  padding: "2px",
}));

const DirtyRowsLabelStyled = styled("div")({
  display: "inline-block",
});

const DirtyRowsLabel = ({
  downloadPartId,
  rowCount,
  dirtyRowCount,
  dirtyRowStatus,
  finishedDateExceeds30days,
  newMethod = false,
  onToggleDetailPanel,
}: DirtyRowsLabelProps) => {
  if (dirtyRowStatus === "none") return null;
  return (
    <DirtyRowsLabelStyled>
      <DownloadDirtyFiles
        downloadPartId={downloadPartId}
        finishedDateExceeds30days={finishedDateExceeds30days}
        dirtyRowStatus={dirtyRowStatus}
        newMethod={newMethod}
        dirtyRowCount={dirtyRowCount}
        onToggleDetailPanel={onToggleDetailPanel}
      >
        {exists(dirtyRowCount) ? (
          <DirtyRowsText rowCount={rowCount} dirtyRowCount={dirtyRowCount} newMethod={newMethod} />
        ) : (
          <span data-test-label={newMethod ? "total-new-dirty-rows" : "total-dirty-rows"}>
            {dirtyRowStatus === "all" ? "100% dirty" : "some dirty rows"}
          </span>
        )}
      </DownloadDirtyFiles>
    </DirtyRowsLabelStyled>
  );
};

const DirtyRowsText = ({
  rowCount,
  dirtyRowCount,
  newMethod,
}: Pick<DirtyRowsLabelProps, "rowCount" | "dirtyRowCount"> & { newMethod?: boolean }) => {
  const perDirtyRows = toPercentValue({ n: dirtyRowCount, d: rowCount });
  return (
    <Typography
      data-test-label={newMethod ? "total-new-dirty-rows" : "total-dirty-rows"}
      variant="body.xSmall.regular"
      sx={{
        color: "inherit",
      }}
    >
      {!perDirtyRows || perDirtyRows < 1 ? `${dirtyRowCount} dirty` : `${perDirtyRows}%  dirty`}
    </Typography>
  );
};

const CompletedStatusColumn = ({
  status,
  dirtyRowStatus,
  newDirtyRowStatus,
  rowCount,
  downloadPartId,
  finished,
  newDirtyRowCount,
  dirtyRowCount,
  statusColumnWidth,
  onToggleDetailPanel,
}: CompletedStatusColumnProps) => {
  if ((dirtyRowStatus === "none" || !exists(dirtyRowStatus)) && (!newDirtyRowStatus || newDirtyRowStatus === "none")) {
    return <StatusColumn status={status || "completed"} rowCount={rowCount} />;
  }
  return (
    <DirtyRowsStatusColumn
      status={status}
      dirtyRowStatus={dirtyRowStatus}
      rowCount={rowCount}
      downloadPartId={downloadPartId}
      finished={finished}
      dirtyRowCount={dirtyRowCount}
      newDirtyRowCount={newDirtyRowCount}
      newDirtyRowStatus={newDirtyRowStatus}
      statusColumnWidth={statusColumnWidth}
      onToggleDetailPanel={onToggleDetailPanel}
    />
  );
};

export { CompletedStatusColumn, DirtyRowsText, StatusColumn };
